import { useCallback, useEffect, useRef, useState } from 'react';
import type { AppResult } from '../core/result/appResult';
import { UiStates, type UiState } from './uiState';

/**
 * Ejecuta una operación de repositorio y la expone como `UiState`.
 *
 * Descarta el resultado de una petición que ya no es la última: cambiar de filtro dos veces
 * seguidas no puede dejar en pantalla la respuesta de la primera, que es como aparecen los
 * resultados "fantasma" que no corresponden a lo que el usuario pidió.
 *
 * @param toState convierte el dato en estado, para poder distinguir "lista vacía" de "éxito".
 */
export function useAsync<T, S = T>(
  operation: () => Promise<AppResult<T>>,
  deps: readonly unknown[],
  toState: (data: T) => UiState<S> = (data) => UiStates.success(data as unknown as S),
): { state: UiState<S>; reload: () => void } {
  const [state, setState] = useState<UiState<S>>(UiStates.loading);
  const [nonce, setNonce] = useState(0);
  const requestId = useRef(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    const id = ++requestId.current;
    let active = true;
    setState(UiStates.loading());

    operation().then((result) => {
      if (!active || id !== requestId.current) return;
      setState(result.ok ? toState(result.data) : UiStates.error(result.error));
    });

    return () => {
      active = false;
    };
    // `operation` y `toState` se redefinen en cada render; las dependencias reales son las que
    // declara quien llama.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { state, reload };
}

/** Debounce para la búsqueda por texto: una petición por pausa de escritura, no por tecla. */
export function useDebounced<T>(value: T, delayMs = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}
